import React, { useEffect, useState, useCallback, ReactElement } from 'react';
import { Table, Spin } from 'antd';
import { LogEntry } from '../../types';
import { get } from 'lodash';

// Notes: each api MUST HAVE offset as first params to make this component work as expected
interface WealthieTableProps {
  columns: Array<any>;
  api: Function;
  rowKey: string;
  // call when page change
  onChangeCallBack?: Function;
  // first time call api
  onFirstFetch?: Function;
  extraApiParams: Array<string | any>;
  isShowLoadingOnStart?: boolean;
  filterData?: string;
  filterPagination?: string;
}

export function WealthieTable({
  columns,
  api,
  rowKey,
  onChangeCallBack,
  extraApiParams,
  isShowLoadingOnStart,
  onFirstFetch,
  filterData,
  filterPagination,
}: WealthieTableProps): ReactElement {
  const [tableData, setTableData] = useState<Array<LogEntry>>([]);
  const [current, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchTableData() {
      isShowLoadingOnStart && setLoading(true);
      try {
        const res = await api(0, 10, ...extraApiParams);
        const tableData = filterData ? get(res, filterData, []) : res.data;
        const totalCounts = get(
          res,
          filterPagination || 'pagination.totalCount',
          1,
        );
        setTotal(totalCounts);
        setTableData(tableData);
        onFirstFetch && onFirstFetch(tableData);
        isShowLoadingOnStart && setLoading(false);
      } catch (error) {
        isShowLoadingOnStart && setLoading(false);
        setTableData([]);
      }
    }

    fetchTableData();
  }, [
    api,
    extraApiParams,
    isShowLoadingOnStart,
    onFirstFetch,
    filterData,
    filterPagination,
  ]);

  const onPaginationChange = useCallback(
    async (pagination: any) => {
      onChangeCallBack && onChangeCallBack();

      setLoading(true);
      try {
        const res = await api(
          pagination.pageSize * (pagination.current - 1),
          pagination.pageSize,
          ...extraApiParams,
        );
        setPageSize(pagination.pageSize);
        setTableData(filterData ? get(res, filterData, []) : res.data);
        setCurrentPage(pagination.current);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    },
    [api, extraApiParams, onChangeCallBack, filterData],
  );

  return (
    <Spin spinning={isLoading} tip="Loading data...">
      <Table
        onChange={onPaginationChange}
        dataSource={tableData}
        rowKey={rowKey}
        pagination={{
          total,
          pageSize,
          current,
        }}
        columns={columns}
      />
    </Spin>
  );
}

export default WealthieTable;
